import React from 'react';
import { Link } from 'react-router-dom';
import BlogLayout from './BlogLayout';
import SEO from '../components/SEO';
import { ArticleStructuredData } from '../components/StructuredData';
import { legacySocialImage } from './legacyPosts';
import LegacyArticleArt from './LegacyArticleArt';

export default function MendelsohnInterview() {
  return (
    <BlogLayout>
      <SEO
        title="What Does a Sales Engineer Do at a Tech Startup?"
        description="An in-depth conversation about the role of Solutions Engineers in technical pre-sales, covering the 5Ds of data, the 3Ps framework, and life at a tech startup."
        type="article"
        url="https://www.greyfielddata.com/blog/sales-engineer-interview"
        image={legacySocialImage('sales-engineer-interview')}
        publishedTime="2024-11-30"
        keywords={['sales engineer', 'solutions engineer', 'pre-sales', 'tech startup', 'data ecosystem', 'career']}
      />
      <ArticleStructuredData
        headline="What Does a Sales Engineer Do at a Tech Startup?"
        description="An in-depth conversation about the role of Solutions Engineers in technical pre-sales, covering the 5Ds of data, the 3Ps framework, and life at a tech startup."
        url="https://www.greyfielddata.com/blog/sales-engineer-interview"
        image={legacySocialImage('sales-engineer-interview')}
        datePublished="2024-11-30"
      />
      <article className="prose prose-lg max-w-none">
        {/* Article Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            What Does a Sales Engineer Do at a Tech Startup?
          </h1>
          <div className="flex items-center gap-4 text-gray-600 mb-2">
            <span>Nov 30</span>
            <span>•</span>
            <span>Written By Linus Tse</span>
          </div>
          <p className="text-gray-500 italic">
            Originally published on Linus' old website - The Regression
          </p>
        </div>

        {/* Article Content */}
        <LegacyArticleArt slug="sales-engineer-interview" />

        <div className="text-gray-800 leading-relaxed space-y-6">
          <p>
            I recently had a chance to interview Mendelsohn Chan about what a solutions engineer does and his perspective on the wider data industry from working in technical pre-sales at a late-stage start-up in the data and AI space.
          </p>

          <p>
            It was a great conversation and if anyone's looking to learn more about pre-sales or learn more about the wider data ecosystem then this is a must-read!
          </p>

          <p>
            Mendelsohn's background lies in consulting and pre-sales for a few different consulting and technology companies across Canada, the US, Australia and Asia.
          </p>

          <p>
            On a more personal note, I've had the opportunity to work with Mendelsohn and he's taught me so much of what I know today. He's one of the hardest workers I know and is extremely dedicated to his craft. Most importantly, he's always radiating positive energy and extremely supportive and always willing to help anyone.
          </p>

          <p>Enjoy!</p>

          <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">Table of Contents</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Pre-Sales Overview</li>
            <li>The Data Ecosystem (5 Ds Framework)</li>
            <li>Clients - Enterprise, Commercial, Digital-Native Businesses</li>
            <li>The Sales Motion (3Ps Framework)</li>
            <li>The 6 Activities of a Solutions Engineer</li>
            <li>Life at a Tech Start-Up and in Singapore</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">Part 1: Pre-Sales Overview</h2>

          <p>
            <strong>The Regression:</strong> What does a Solutions Engineer do?
          </p>

          <p className="bg-gray-100 p-4 rounded-lg text-sm">
            Note: Different companies call the role different names, but these job titles all mean the same thing: Solutions Engineer, Solutions Architect, Sales Engineer, Technical Presales Consultant
          </p>

          <p>
            <strong>Mendelsohn Chan:</strong> My job is to support the account executive, also known as the salesperson, in all technical matters related to the product. So this could manifest itself in a couple of ways like conducting live demos of the product, doing data architecture workshops to pitch solutions, a competitive analysis, designing POCs (proof of concept). And when I say design, it's doing the business value analysis, building out the evaluation criteria all the way to actually implementing the actual data pipelines from a hands on perspective.
          </p>

          <p>
            <strong>The Regression:</strong> Are you a salesperson with technical ability? Is that fair to say?
          </p>

          <p>
            <strong>Mendelsohn Chan:</strong> See, I don't really sell because there's an actual dedicated salesperson who's job is to build relationships with the customer and orchestrate the end-to-end sales lifecycle. My job is primarily to serve as a technical subject-matter expert and to persuade the prospect that the solution we are offering would be of value to their organization. Typically, I map to someone called the 'technical buyer' and work with them primarily.
          </p>

          <p>
            <strong>The Regression:</strong> There's a technical buyer and there's a non-technical buyer?
          </p>

          <p>
            <strong>Mendelsohn Chan:</strong> That's right. A non-technical buyer is also known as an "economic buyer" in pre-sales. It's usually the more powerful person, like the CEO or COO, who signs the checks. These people are not usually technical and don't have the background in data or AI. And so they consult with someone like the CIO or the Director of Analytics or the Director of Engineering. And my job is to persuade those people that the solution that I'm putting forward can best meet their needs out of the alternatives in the market.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">Part 2: The Data Ecosystem (and the 5D's)</h2>

          <p>
            <strong>The Regression:</strong> Can you tell me a bit about the alternatives? Which area in the data ecosystem do you operate in?
          </p>

          <p>
            <strong>Mendelsohn Chan:</strong> Yeah, generally when I think about the Data and AI landscape, I think of it in the 5Ds. They are:
          </p>

          <ol className="list-decimal pl-6 space-y-3">
            <li>
              <strong>Data Engineering:</strong> Dbt, Fivetran, Matillion, Talend
            </li>
            <li>
              <strong>Data Science & Machine Learning:</strong> DataRobot, H20.AI, Dataiku
            </li>
            <li>
              <strong>Data Warehousing:</strong> Snowflake, Databricks, BigQuery, Redshift, Azure/Fabric
            </li>
            <li>
              <strong>Data Analytics:</strong> Tableau, Power BI, Looker
            </li>
            <li>
              <strong>Data Governance:</strong> Collibra, Alation
            </li>
          </ol>

          <p>
            I work in the Data Warehousing bucket of the ecosystem primarily, but as you can imagine, the lines between these categories overlap and get blurred sometimes. But for simplicity's sake the biggest players in the data warehousing market these days are: Snowflake, Databricks, BigQuery, Redshift, and Microsoft Fabric which I call the "big five in data warehousing".
          </p>

          <p>
            And frankly nowadays it's really a two horse race between Snowflake and Databricks, similar to the BI space where it's really Tableau and Power BI now. I'd say between the two, they collectively capture a majority of the new market.
          </p>

          <p>
            I should qualify my previous statement in that this trend applies primarily to more modern organizations. Companies who are migrating from on-premise data centers to the cloud, and so Databricks and Snowflake are well-positioned to capture market share away from traditional vendors like Cloudera Hadoop, Teradata, Netezza, and Oracle Exadata.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
            Part 3: The Differences in Enterprise, Commercial and DNB Clients
          </h2>

          <p>
            <strong>The Regression:</strong> What types of companies do you typically work with?
          </p>

          <p>
            <strong>Mendelsohn Chan:</strong> Generally speaking, tech SaaS companies breakdown the market into various geographic territories (e.g. APAC, EMEA, AMER) and segments broken down by the size of the company (e.g. Enterprise, Commercial, DNB - Digital Native Businesses).
          </p>

          <p>
            So let's start with how companies break these down. Various companies do it differently but typically it's based off of a headcount or a revenue cut-off. For instance, any company with less than 500 employees would fall under the Commercial bucket. Anything beyond that would be enterprise. Alternatively, if it was based on revenue then any company with less than $100 million in revenue would be on the Commercial side.
          </p>

          <p>
            For DNBs, this is a very special category that only exists in various tech companies. And the reason we're cutting a slice for these guys is because these represent the most mature and most sophisticated companies when it comes to data and AI. Now it goes without saying that there's FAANG in there but even other companies like an Instacart or a Shopify, or a young startup, we would typically classify them as a DNB company.
          </p>

          <p>
            <strong>The Regression:</strong> What's the differences in the various segments when you work with them?
          </p>

          <p>
            <strong>Mendelsohn Chan:</strong> So the main difference is the sales motion which basically means how you sell to these companies. Everything factors into the sales motion like the strategies, the predefined steps and the types of conversations that we have. An example would be, how you would sell to a TD Bank (Canadian bank) is so different from how you would sell to a DoorDash or an Airbnb. These companies are fundamentally different in terms of how they operate and so as a pre sales engineer, you have to tailor your strategy.
          </p>

          <p>
            For enterprise and commercial, I think the sales motion and the strategy is similar in that I'm generally talking to economic buyers who are typically not technical so there's a big focus on business value. They're asking questions like, "What if I buy Databricks or Snowflake? How would that generate more business value to me?" I could tell them this 100 line SQL query could be faster but you also have to articulate the implications. So for example, I could position it like a faster time to market. With these companies, it's all about tying the technical feature to a business outcome.
          </p>

          <p>
            However, for DNBs, I'm generally selling to actual technical engineers who are a lot more focused on the technical capabilities than the business value. Of course I'm generalizing here, and both types of personas do still care about technical capabilities and business value; but this is how would I would simplify it.
          </p>

          <p>
            <strong>The Regression:</strong> What's a real-world example of this?
          </p>

          <p>
            <strong>Mendelsohn Chan:</strong> In the data warehousing space, a lot of companies use something called batch ETL which basically means like loading a bunch of CSVs which kick-starts the ETL process. If I was working with a DNB, I'd be pitching streaming ETL which is more of a cutting edge feature. It basically means whenever a new sale is recorded in the relational database, it flows through directly to the warehouse. But when I work with enterprise companies, I know that this newer feature is more of a nice to have and I have to be very clear in explaining the business value.
          </p>

          <p>
            So coming back to the solutions engineer, there's typically two skill sets you need, the technical skills and non-technical skills. When working with enterprise and commercial customers, you'll need a greater emphasis on things like communication and selling skills. Whereas for DNBs, the focus is shifted and the focus needs to be higher for technical skills and less-so things like communication and selling ability. Obviously it goes without saying that you still need to have a baseline level in both areas.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
            Part 4: The Sales Motion and the 3Ps of Sales
          </h2>

          <p>
            <strong>The Regression:</strong> How does the sales motion begin and what's a Solution Engineer's role in it?
          </p>

          <p>
            <strong>Mendelsohn Chan:</strong> Anything could happen, man. It could be a formalized RFP. It could just be someone starting a free trial or it could be an account executive reaching out to a potential prospect.
          </p>

          <p>
            I typically get involved after a few calls have happened and when initial qualification has been done and the things start to get more deep and more technical.
          </p>

          <p>
            <strong>The Regression:</strong> What does qualification mean? Like whether they have the budget or whether they're serious?
          </p>

          <p>
            <strong>Mendelsohn Chan:</strong> Right. Or sometimes they'll be asking for things that my company doesn't do, like data observability or security and, you know, things like a Datadog or some other cyber security tool comes to mind. So we're not the right fit for the use case. Only when the opportunity gets qualified will they then bring in the solutions engineer.
          </p>

          <p>
            From there, the sales process begins by conducting discovery. I'll talk to them and understand the requirements and pain points. For me personally, I have a bunch of go to questions I ask like, why are we having this conversation now? What's the impetus or driver for change? What's not working with your current tech stack? Or they're just looking to upgrade their current platform to something more powerful. But long story short, I need to know the motivation as to why we're having this conversation to begin.
          </p>

          <p>
            <strong>The Regression:</strong> Ok, that makes sense and it sounds very similar to consulting where you're doing a lot of discovery initially. What happens next then?
          </p>

          <p>
            <strong>Mendelsohn Chan:</strong> I'm going to introduce another acronym or framework which I call the 3 P's. Even though I'm a technical resource, a lot of this job comes down to human psychology so I need to know which levers to pull to resonate with the customer and relate to them better.
          </p>

          <ol className="list-decimal pl-6 space-y-2">
            <li><strong>Price</strong></li>
            <li><strong>Performance</strong></li>
            <li><strong>Productivity</strong></li>
          </ol>

          <p>
            So based on my personal experience, for each client, I have to emphasize either one of these categories or maybe use all three of three cards at the same time.
          </p>

          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">1. Price</h3>
          <p>
            Price just means in the simplest form that the customer has issues with their current cost. So maybe they're unhappy that they're spending millions of dollars for their current warehouse and now they're talking to various vendors out there to potentially lower the cost of their data warehouse spend. In this first P, the impetus for change is the rising cost of their IT spend. And that's the motivation I have to tailor fit my messaging and say, hey, you know, using another solution could help lower TCO which stands for Total Cost of Ownership. By that, I mean that you have to factor in other indirect variables of cost like man hours and effort.
          </p>

          <p>
            For example, the solution I'm pitching from a licensing cost perspective could be exactly the same or actually maybe even be a bit more expensive. However, if we are able to shave off X hours per week by eliminating low-end tasks like patching the operating system, backing-up servers then we're saving X amount of dollars per year in labor costs and productivity gains. As a Solutions Engineer, I need to understand the full picture to help them articulate the business value story which is centered around TCO but for simplicity in this framework, I just call it price.
          </p>

          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">2. Performance</h3>
          <p>
            The second P is called Performance. Now again, performance here could be many things. More often than not, companies are complaining that their current data warehouse, for example, is super slow. Like, imagine connecting your Tableau dashboard to your data warehouse and whenever someone clicks a filter, it takes a full minute to refresh. Or when someone writes a long SQL query and it takes a long time to generate the result. That's one aspect of performance.
          </p>

          <p>
            The other aspect is from a time to market perspective meaning from the ingestion of the raw data all the way through the ETL process and finally to serving the data in the dashboard or machine learning model. Companies want to cut that end to end duration and make the data be available in a more timely manner. So there's different aspects to think through here, but I generally roll them up into a single bucket of Performance.
          </p>

          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">3. Productivity</h3>
          <p>
            And the last P is for Productivity. Productivity can be many things and for me personally, it just means like ease of use. So for example, in managing an on premise data warehouse, they have to do a lot of low level maintenance tasks that simply go away with a SaaS Cloud solution like Databricks or Snowflake. So which means that we address the ease of use or pain point. Perhaps in your world, an example of this would be Tableau versus Power BI. Personally, I think Tableau is easier to use. So even if Tableau is more expensive than Power BI, we can convey the value through better productivity.
          </p>

          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">3Ps Summary</h3>
          <p>
            So long story short, these are thee three main levers I use when selling. Based on experience, a customer will almost always emphasize one particular pain point over the others. Sometimes it'll be all three, sometimes two or three, and again, it just depends on how well you do the discovery right. But more often than not, there's always that one P that stands out for the customer.
          </p>

          <p>
            <strong>The Regression:</strong> That makes a lot of sense. It sounds like this process takes a while, maybe a couple months?
          </p>

          <p>
            <strong>Mendelsohn Chan:</strong> Nooooo. For most enterprise companies, the average sales cycle takes more than one full year. It's a very long process. For commercial companies, it can still span months. And right before the actual sale, we go through what's called an Infosec review with their information security department. So to get clear around security and compliance so it could be non-technical matters like you know, do you have GDPR certification? PIPEDA ISO certification, all the way to the granular weeds of networking like which firewalls are open and which ports are blocked. Do you have public and private IP addresses protected?
          </p>

          <p>
            Just to go through the end to end Infosec review, it's a very labor intensive and tedious review.
          </p>

          <p>
            <strong>The Regression:</strong> I don't know much about the process. Is there a separate team that works on that or is it you?
          </p>

          <p>
            <strong>Mendelsohn Chan:</strong> It's primarily the Solutions Engineers role. We have resources like a repository of common questions which do help us out but it's still a lot of work. So I'll give you a random one which no one is expected to remember but for some reason I do because I encounter it all the time. Things like which encryption protocol do you use for data in-transit and at rest? Or which TLS version do you use? These are just methods to encrypt your data while it's flowing through the network.
          </p>

          <p>
            <strong>The Regression:</strong> And then 12-18 months later, the contract gets signed and then that's where the actual onboarding happens. After the sale is made, is your job complete?
          </p>

          <p>
            <strong>Mendelsohn Chan:</strong> Nope. It's absolutely within my ballpark. I literally do this for my job. So I will work with the IT department to profile their existing data warehouse topology. So for example for Redshift I need to know how many cores, how many RAM, how much data do you have in terms of volume, variety and velocity. How many ETL scripts are running there and we need to map it all.
          </p>

          <p>
            To talk about the scale here, we're talking about thousands of scripts. There are tools out there called code conversion tools that allow you to convert Amazon Redshift PostgreSQL syntax to Databricks Spark SQL syntax for instance. There's also drag and drop tools which are primarily XML based.
          </p>

          <p>
            Then, we still have to create a demand plan since we charge base on compute, consumption. You also have a storage cluster running for 1 hour a day for an ETL job which is multiplied by 30 days and twelve months. You also have to take into account growth of data over time. From 1GB this month to 1.1 to 1.2 and so on. That's actually the toughest part of this job, doing the forecasting based on how much "electricity" they're going to consume.
          </p>

          <p>
            <strong>The Regression:</strong> And you forecast for how long? And you're on this client for how long?
          </p>

          <p>
            <strong>Mendelsohn Chan:</strong> On an ongoing basis and because things change. You help them make a plan and you're with this client forever.
          </p>

          <p>
            And to me, the first line of defense is always the solutions engineer. Ideally, in a perfect world you would just point them to the right resources or documentation but sometimes it requires you to get hands-on and to help them fix it, and as a solution engineer, that's my job!
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
            Part 5: The 6 Functional Activities of a SE
          </h2>

          <p>
            <strong>The Regression:</strong> Ok, so clearly as a Solutions Engineer, you wear a lot of hats and do a lot of different things. Is there anything else you do?
          </p>

          <p>
            <strong>Mendelsohn Chan:</strong> I think of it in 6 buckets generally. They are:
          </p>

          <ol className="list-decimal pl-6 space-y-2">
            <li>Use Case Discovery and Qualification</li>
            <li>Product Demos</li>
            <li>Product and Deal Sizing</li>
            <li>Formulating and Implementing POCs</li>
            <li>Field Marketing</li>
            <li>Business Value Analysis</li>
          </ol>

          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">
            1. Use Case Discovery and Qualification
          </h3>
          <p>
            The first is use case discovery and qualification which I've talked about earlier. It's basically learning what is the impetus for change, and how do we position my solution in a way to address the client's pain points.
          </p>

          <p>
            To qualify the opportunity, you sometimes also have to qualify yourself out. I gave an example with Datadog earlier but another example would be sometimes companies don't want to move to the Cloud yet. So even if they have a pain point which you can address, the buck stops there because with a cloud native solution, there's no point in talking even further. Or if they have a limited budget like, you know, $10,000, which may not be enough then we wouldn't proceed with the sales opportunity.
          </p>

          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">2. Product Demos</h3>
          <p>
            Point 2 is fairly straightforward. As an SE, you do product demos. I guess the only thing I'd add here is we tailor fit the demo based off of a client's needs. So we try to find a relevant use case to make the demo resonate with the customer. I try to use customized data sets for every single vertical industry because I wouldn't want to demo a data set about a superstore to a company like Rogers (Canadian Telecommunications company), right? In that case, I'd use a Teleco relevant data set or use case like customer churn, upselling and so on.
          </p>

          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">3. Product and Deal Sizing</h3>
          <p>
            Product sizing is about sizing the opportunity of the deal. In the world of data warehousing specifically, it's more complex than BI tools like Tableau because with BI tools, it's fairly straightforward to do this since it's based off the number of licenses that the client wishes to purchase. So, that'd be like 100 creative licenses multiplied by $70.00 a month. For data warehouses, the size of the opportunity is based off my framework called the 3 Vs of data. Volume, Velocity, Variety.
          </p>

          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Volume</strong> - How large are your data sets? Are these terabytes scale or GB scale data?
            </li>
            <li>
              <strong>Velocity</strong> - How often do you need to ingest the data? Are you OK doing once a day ETL jobs or do you want to real time stream? This is a huge difference.
            </li>
            <li>
              <strong>Variety</strong> - What type of data sets are you ingesting? Salesforce? Oracle? SAP?
            </li>
          </ul>

          <p>
            All of these three factors would affect the size of your data warehouse deployment in terms of whether the deal would be worth $10K, $25K or several $1,000,000s.
          </p>

          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">
            4. Formulating and Implementing Proof of Concepts
          </h3>
          <p>
            After you've done the qualification, then I work with the customer to implement the solution in a lightweight manner called the POC. This involves taking a bunch of data sets, ingesting it, building some ETL scripts and visualizing it and connecting it to a table. This is a very simplistic way of putting it, but in the process of building these pipelines and writing the code, my job is to always tie it back to the business value.
          </p>

          <p>
            If I were to share an industry-specific jargon for the readers, in warehousing we have this thing called bake-offs. A bake-off is where your goal is to overthrow the incumbent solution by showing either you are cheaper or better or faster, which is basically the 3Ps (Price, Performance, Productivity).
          </p>

          <p>
            For example, let's say a prospect had an existing data warehouse ABC, was running ETL scripts once a day and ingesting 50 gigabytes of data. I would replicate those exact same variables with my platform. To make the experiment fair, I would ingest the same data set with the same volume with the 50gb and actually build the exact same pipeline in my platform. And then I'd showcase the advantages of building the ETL job in my platform.
          </p>

          <p>
            There's a term in economics called ceteris paribus which is a Latin term meaning all things being equal. It's just a fancy way of saying keeping the variables the same which is what we do.
          </p>

          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">5. Field Marketing</h3>
          <p>
            This is easy. It just means doing events basically. It's a fun part of the job meeting people so going to like fairs, summits and conferences and manning the booth a summit or networking with people in the industry, field marketing or doing training sessions in-person at various companies.
          </p>

          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">6. Business Value Analysis</h3>
          <p>
            The last point is about business value analysis. So this means quantifying the financial benefits. I'd refer back to earlier when I explained the 3Ps framework.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
            Part 6: Tech Start-Ups and Working in Singapore
          </h2>

          <p>
            <strong>The Regression:</strong> What's it like working at a fast-growing tech startup?
          </p>

          <p>
            <strong>Mendelsohn Chan:</strong> Oh, it's been an unbelievable ride so far as the company has been growing at a blistering pace. In fact, whenever a new employee joins the company, we always welcome them with "Welcome to the rocketship!". The people I've worked with so far are all top-notch, and the bar is really high since we all had to go through over 6 to 8 rounds of interviews.
          </p>

          <p>
            <strong>The Regression:</strong> I've heard you say that in the past though about other companies you've worked for...
          </p>

          <p>
            <strong>Mendelsohn Chan:</strong> The other places I've worked for have been really good, but every time I've changed companies, the bar keeps getting raised to new levels I didn't know existed. To use an NBA reference, I've worked for companies in the past that are like the all NBA second team but these guys, they're the NBA first team.
          </p>

          <p>
            <strong>The Regression:</strong> Why do you say that? Can you give an example?
          </p>

          <p>
            <strong>Mendelsohn Chan:</strong> Intellectual prowess and the ability to process information in context, in real time. So I have a colleague who has a PhD in graph databases and he's published five papers on graph theory and he's made his own Python library from scratch. That's crazy.
          </p>

          <p>
            <strong>The Regression:</strong> Is he also a Solutions Engineer who works with you?
          </p>

          <p>
            <strong>Mendelsohn Chan:</strong> So I'm like the family doctor and he's like the specialist solutions engineer. We have a pool we can reach out to who are specialists depending on the technical topic in discussion.
          </p>

          <ul className="list-disc pl-6 space-y-2">
            <li>Data Science and Machine Learning</li>
            <li>Networking and Infrastructure</li>
            <li>Realtime Streaming</li>
          </ul>

          <p>
            <strong>The Regression:</strong> I want to end by asking what its like working in Singapore? How are things on that side of the world?
          </p>

          <p>
            <strong>Mendelsohn Chan:</strong> There are very different expectations in Singapore as far as work culture goes, and people work a lot harder and longer. I work 50 to 60 hours a week on average which would be considered a lot compared to North America or Europe. However, I love being in Singapore for things like:
          </p>

          <ul className="list-disc pl-6 space-y-2">
            <li>Efficiency of the commute</li>
            <li>Cleanliness of the environment</li>
            <li>Super safe with one of the lowest crime rates in the world</li>
            <li>Affordable and delectable food with sub $5 meals at the hawker centers</li>
          </ul>

          <p>
            <strong>The Regression:</strong> Thanks for your time, Mendelsohn!
          </p>
        </div>

        <Link to="/blog" className="inline-block mt-12 text-blue-600 hover:text-blue-800">
          ← Back to blog
        </Link>

        {/* Related Posts */}
        <div className="mt-16 pt-8 border-t border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Articles</h2>
          <div className="grid gap-6 md:grid-cols-2">
            <Link to="/blog/snowflake-explained" className="block p-6 border border-gray-200 rounded-lg hover:border-blue-500 transition">
              <h3 className="font-bold text-gray-900 mb-2">What Is Snowflake? A Non-Technical Guide</h3>
              <p className="text-gray-600 text-sm">Learn what makes Snowflake special</p>
            </Link>
            <Link to="/blog/tableau-vs-powerbi" className="block p-6 border border-gray-200 rounded-lg hover:border-blue-500 transition">
              <h3 className="font-bold text-gray-900 mb-2">Tableau vs Power BI</h3>
              <p className="text-gray-600 text-sm">Compare the two leading BI platforms</p>
            </Link>
          </div>
        </div>
      </article>
    </BlogLayout>
  );
}
